import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router";

import { ProcedureContext } from "@/context/ProcedureContext";
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	UniqueIdentifier,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
	Box,
	Chip,
	Divider,
	Paper,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import rehypeRaw from "rehype-raw";

import { Translate } from "@/components/Application/Translate";
import { useFetchChecklist } from "@/components/Procedures/API/ChecklistQueries";
import PreviewStep from "@/components/Procedures/Components/CreateStep/PreviewStep";
import { CheckpointType } from "@/components/Procedures/Types/ManualChecklistTypes";

import { ErrorToast } from "@/helpers/alerts/AlertToast";

import { NodeSort } from "@/model/Procedure/ProcedureNodeSteps";

import { UpsertProcedureNodeSteps } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

const ChecklistPreview = () => {
	const theme = useTheme();
	const { id, schemeId } = useParams<RouteParams>();
	const checklistId = Number(schemeId);
	const { data: editorContent } = useFetchChecklist(checklistId);

	const { activeStep, setActiveStep } = useContext(ProcedureContext);

	// Keep ids in the order shown
	const [items, setItems] = useState<UniqueIdentifier[]>([]);

	// dnd-kit sensors
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	// Refs for “scroll into view” when activeStep changes
	const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});

	// Build a stable map: stepId -> step
	const stepsById = useMemo(() => {
		const map: Record<string, any> = {};
		(editorContent?.Steps ?? []).forEach((s) => {
			map[String(s.Id)] = s;
		});
		return map;
	}, [editorContent?.Steps]);

	// Initialize items when steps arrive
	useEffect(() => {
		if (editorContent?.Steps?.length) {
			setItems(editorContent.Steps.map((s) => s.Id));
		} else {
			setItems([]);
		}
	}, [editorContent?.Steps]);

	// Scroll active step into view if needed
	useEffect(() => {
		if (activeStep == null || activeStep < 0) return;
		const idAtIndex = items[activeStep];
		if (idAtIndex == null) return;
		const el = stepRefs.current[String(idAtIndex)];
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const inView = rect.top >= 64 && rect.bottom <= window.innerHeight; // pad for sticky header
		if (!inView) el.scrollIntoView({ behavior: "smooth", block: "center" });
	}, [activeStep, items]);

	const saveChecklist = UpsertProcedureNodeSteps();

	const onSave = async (_addNew: boolean, newSortOrder: NodeSort[]) => {
		if (!editorContent?.Steps?.length) return;
		try {
			await saveChecklist({
				ProcedureId: Number(id),
				ChecklistId: checklistId,
				Name: editorContent.Name ?? "",
				Ingress: editorContent.Ingress ?? "",
				NodeSteps: editorContent.Steps,
				NewStep: false,
				NewCheckList: false,
				NodeSortOrder: newSortOrder,
				IsMaterialHandling: false,
			});
		} catch (e) {
			ErrorToast(
				<Translate String="Manuel.Checklist.Sort.Error">
					Error during sort action
				</Translate>,
			);
			console.error(e);
		}
	};

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over) return;

		if (active.id === over.id) {
			setActiveStep(items.indexOf(active.id));
			return;
		}

		setItems((prev) => {
			const oldIndex = prev.indexOf(active.id);
			const newIndex = prev.indexOf(over.id);
			if (oldIndex < 0 || newIndex < 0) return prev;

			const updated = arrayMove(prev, oldIndex, newIndex);
			setActiveStep(newIndex);

			const newSortOrder: NodeSort[] = updated.map((stepId, idx) => ({
				Id: Number(stepId),
				Index: idx,
			}));
			onSave(false, newSortOrder);

			return updated;
		});
	};

	const stepCount = editorContent?.Steps?.length ?? 0;

	return (
		<Paper
			elevation={0}
			sx={{
				px: 2.5,
				py: 2.5,
				position: "sticky",
				top: 0,
				zIndex: 1,
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: 2,
				bgcolor: "background.paper",
			}}
		>
			{/* Header */}
			<Box
				onClick={() => setActiveStep(-1)}
				sx={{ cursor: "default", mb: 2 }}
				aria-label="Checklist preview header"
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={1.5}
					sx={{ mb: 1 }}
				>
					<Typography variant="h5" fontWeight={700}>
						<Translate String="Manual.Checklist.Preview.Header">
							Preview
						</Translate>
					</Typography>
					<Chip
						label={`${stepCount} ${stepCount === 1 ? "entry" : "entries"}`}
						size="small"
						sx={{ fontWeight: 600 }}
					/>
				</Stack>

				<Typography variant="h4" component="h2" sx={{ mb: 0.5 }}>
					{editorContent?.Name}
				</Typography>

				{!!editorContent?.Ingress && (
					<Box
						sx={{
							color: "text.secondary",
							"& p": { m: 0, lineHeight: 1.6 },
						}}
					>
						<ReactMarkdown rehypePlugins={[rehypeRaw]}>
							{editorContent.Ingress}
						</ReactMarkdown>
					</Box>
				)}

				<Divider sx={{ mt: 2 }} />
			</Box>

			{/* Empty state */}
			{!stepCount && (
				<Box
					sx={{
						py: 4,
						textAlign: "center",
						color: "text.secondary",
						border: `1px dashed ${theme.palette.divider}`,
						borderRadius: 2,
					}}
				>
					<Typography variant="body1" fontWeight={600}>
						<Translate String="Manual.Checklist.Preview.Empty">
							No steps yet
						</Translate>
					</Typography>
					<Typography variant="body2">
						<Translate String="Manual.Checklist.Preview.EmptyHint">
							Add steps on the left, then drag to reorder them here.
						</Translate>
					</Typography>
				</Box>
			)}

			{/* Steps list with drag & drop */}
			{!!stepCount && (
				<Box sx={{ pt: 1 }}>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={onDragEnd}
					>
						<SortableContext
							items={items}
							strategy={verticalListSortingStrategy}
							disabled={!editorContent}
						>
							<Stack spacing={1.25}>
								{items.map((itemId, index) => {
									const step = stepsById[String(itemId)];
									if (!step) return null; // safety guard if data changes mid-render

									const stepNumber =
										step.CheckpointType === CheckpointType.Info
											? undefined
											: items
													.slice(0, index)
													.filter(
														(id) =>
															stepsById[String(id)]?.CheckpointType !==
															CheckpointType.Info,
													).length + 1;

									return (
										<Box
											key={String(itemId)}
											ref={(el) => {
												stepRefs.current[String(itemId)] = el;
											}}
										>
											<PreviewStep
												i={index}
												id={itemId}
												step={step}
												checklistId={checklistId}
												importedChecklist={editorContent?.ImportedChecklist ?? false}
												stepNumber={stepNumber}
											/>
										</Box>
									);
								})}
							</Stack>
						</SortableContext>
					</DndContext>
				</Box>
			)}
		</Paper>
	);
};

export default ChecklistPreview;
